"use client";

import { useRouter } from "next/navigation";

import { useAuth } from "@/features/auth";
import { markInboxFlash } from "@/features/inbox/lib/flash-signal";
import { useProjectSwitch } from "@/features/projects/hooks/use-project-switch";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";

import { useMarkRead } from "../../../hooks/use-mark-read";
import { useMarkUnread } from "../../../hooks/use-mark-unread";
import { relativeTime } from "../../../lib/helpers";
import {
  getNotificationActionUrl,
  getNotificationActor,
  getNotificationPreviewText,
  getNotificationProjectId,
} from "../../../lib/metadata";
import type { Notification } from "../../../lib/types";

/** Pull the thread/chat id out of an inbox deep-link URL. */
function inboxConversationIdFromUrl(url: string): string | null {
  try {
    const base =
      typeof window === "undefined" ? "http://localhost" : window.location.origin;
    const params = new URL(url, base).searchParams;
    return params.get("threadId") || params.get("chatId");
  } catch {
    return null;
  }
}

export function useNotificationItem(notification: Notification) {
  const router = useRouter();
  const markRead = useMarkRead();
  const markUnread = useMarkUnread();

  const { selectedProjectId } = useAuth();

  const { switchProjectAsync } = useProjectSwitch();

  const actionUrl = getNotificationActionUrl(notification);
  const targetProjectId = getNotificationProjectId(notification);
  const isUnread = !notification.read_at && !notification.cleared_at;

  async function handleActivate() {
    if (isUnread) markRead.mutate(notification.id);
    if (!actionUrl) return;
    // Candidate-reply notifications flash the message they point to once
    // the inbox opens. Flag it here (a notification-origin signal that a
    // refresh won't reproduce) so a plain reload never flashes.
    if (notification.category === "sequence_replies") {
      const conversationId = inboxConversationIdFromUrl(actionUrl);
      if (conversationId) markInboxFlash(conversationId);
    }
    if (targetProjectId && targetProjectId !== selectedProjectId) {
      try {
        await switchProjectAsync(targetProjectId);
      } catch (err) {
        showToast.error("Project unavailable", {
          description: getApiErrorMessage(err),
        });
        return;
      }
    }
    router.push(actionUrl);
  }

  function handleMarkRead() {
    if (isUnread) markRead.mutate(notification.id);
  }

  function handleMarkUnread() {
    if (!isUnread) markUnread.mutate(notification.id);
  }

  return {
    actionUrl,
    actor: getNotificationActor(notification),
    handleActivate,
    handleMarkRead,
    handleMarkUnread,
    isUnread,
    previewText: getNotificationPreviewText(notification),
    relativeCreatedAt: relativeTime(notification.created_at),
  };
}
